import { createSlice } from '@reduxjs/toolkit';

export interface AppDownloadBannerState {
  showBanner: boolean;
  lastShownDate: string | null;
  dismissed: boolean;
}

const STORAGE_KEY = 'appDownloadBanner';
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const getStoredBannerState = (): Partial<AppDownloadBannerState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to parse stored app download banner state:', error);
  }
  return {};
};

const shouldShowBanner = (): boolean => {
  const stored = getStoredBannerState();

  if (!stored.lastShownDate) {
    return true;
  }

  const lastShown = new Date(stored.lastShownDate).getTime();
  const now = Date.now();
  const timeSinceLastShown = now - lastShown;

  return timeSinceLastShown >= ONE_MONTH_MS;
};

const initialState: AppDownloadBannerState = {
  showBanner: false,
  lastShownDate: null,
  dismissed: false,
  ...getStoredBannerState(),
};

const appDownloadBannerSlice = createSlice({
  name: 'appDownloadBanner',
  initialState,
  reducers: {
    checkAndShowBanner: (state) => {
      if (shouldShowBanner()) {
        state.showBanner = true;
        state.dismissed = false;
      }
    },

    dismissBanner: (state) => {
      state.showBanner = false;
      state.dismissed = true;
      state.lastShownDate = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    },

    hideBanner: (state) => {
      state.showBanner = false;
    },

    resetBannerState: (state) => {
      state.showBanner = false;
      state.lastShownDate = null;
      state.dismissed = false;
      localStorage.removeItem(STORAGE_KEY);
    },
  },
});

export const {
  checkAndShowBanner,
  dismissBanner,
  hideBanner,
  resetBannerState,
} = appDownloadBannerSlice.actions;

export default appDownloadBannerSlice.reducer;
